import type { Key } from "react";
import type { Dashboard, DashboardBreakdown } from "@/types";

import { Segment, Widget } from "@heroui-pro/react";
import { AreaChart } from "@heroui-pro/react/area-chart";
import { BarChart } from "@heroui-pro/react/bar-chart";
import { useState } from "react";

import { aedRounded } from "@/components/sales/shared";

// The theme's chart tokens are one hue light to dark; 1 and 4 are far enough apart to tell two series apart.
const SERIES_A = "var(--chart-1)";
const SERIES_B = "var(--chart-4)";

const DAY = new Intl.DateTimeFormat("en-GB", {
  day: "numeric",
  month: "short",
  timeZone: "UTC",
});
const COMPACT = new Intl.NumberFormat("en", {
  notation: "compact",
  maximumFractionDigits: 1,
});

/** Series dates are Dubai days as YYYY-MM-DD; read them as UTC so they never shift. */
const dayLabel = (value: number | string) =>
  DAY.format(new Date(`${value}T00:00:00Z`));

const axis = { axisLine: false, tickLine: false, fontSize: 12 } as const;

function grainText(grain: Dashboard["range"]["grain"]) {
  return grain === "week" ? "Per week" : "Per day";
}

export function RevenueChart({ data }: { data: Dashboard }) {
  return (
    <Widget>
      <Widget.Header>
        <div className="flex flex-col">
          <Widget.Title>Net revenue and gross profit</Widget.Title>
          <Widget.Description>
            {grainText(data.range.grain)}, fulfilled orders, ex-VAT
          </Widget.Description>
        </div>
        <Widget.Legend>
          <Widget.LegendItem color={SERIES_A}>Net revenue</Widget.LegendItem>
          <Widget.LegendItem color={SERIES_B}>Gross profit</Widget.LegendItem>
        </Widget.Legend>
      </Widget.Header>
      <Widget.Content>
        <AreaChart data={data.series} height={260}>
          <AreaChart.Grid strokeDasharray="3 3" vertical={false} />
          <AreaChart.XAxis
            {...axis}
            dataKey="date"
            minTickGap={24}
            tickFormatter={dayLabel}
          />
          <AreaChart.YAxis
            {...axis}
            tickFormatter={(value: number) => COMPACT.format(value)}
            width={44}
          />
          <AreaChart.Tooltip
            content={
              <AreaChart.TooltipContent
                labelFormatter={dayLabel}
                valueFormatter={(value) => aedRounded(Number(value))}
              />
            }
          />
          <AreaChart.Area
            dataKey="netRevenue"
            fill={SERIES_A}
            fillOpacity={0.12}
            name="Net revenue"
            stroke={SERIES_A}
            strokeWidth={2}
            type="monotone"
          />
          <AreaChart.Area
            dataKey="grossProfit"
            fill={SERIES_B}
            fillOpacity={0.12}
            name="Gross profit"
            stroke={SERIES_B}
            strokeWidth={2}
            type="monotone"
          />
        </AreaChart>
      </Widget.Content>
    </Widget>
  );
}

const ORDER_SERIES = [
  { key: "fulfilledOrders", label: "Fulfilled", color: SERIES_A },
  { key: "openOrders", label: "Open", color: SERIES_B },
  { key: "cancelledOrders", label: "Cancelled", color: "var(--danger)" },
] as const;

export function OrdersChart({ data }: { data: Dashboard }) {
  return (
    <Widget>
      <Widget.Header>
        <div className="flex flex-col">
          <Widget.Title>Orders placed</Widget.Title>
          <Widget.Description>
            {grainText(data.range.grain)}, by where they are now
          </Widget.Description>
        </div>
        <Widget.Legend>
          {ORDER_SERIES.map((s) => (
            <Widget.LegendItem key={s.key} color={s.color}>
              {s.label}
            </Widget.LegendItem>
          ))}
        </Widget.Legend>
      </Widget.Header>
      <Widget.Content>
        <BarChart data={data.series} height={260}>
          <BarChart.Grid strokeDasharray="3 3" vertical={false} />
          <BarChart.XAxis
            {...axis}
            dataKey="date"
            minTickGap={24}
            tickFormatter={dayLabel}
          />
          <BarChart.YAxis {...axis} allowDecimals={false} width={32} />
          <BarChart.Tooltip
            content={<BarChart.TooltipContent labelFormatter={dayLabel} />}
            cursor={{ fill: "var(--surface-secondary)" }}
          />
          {ORDER_SERIES.map((s, index) => (
            <BarChart.Bar
              key={s.key}
              dataKey={s.key}
              fill={s.color}
              name={s.label}
              radius={index === ORDER_SERIES.length - 1 ? [4, 4, 0, 0] : 0}
              stackId="orders"
            />
          ))}
        </BarChart>
      </Widget.Content>
    </Widget>
  );
}

type GroupBy = "supplier" | "category";

/** Top 8 suppliers or categories by one measure, as horizontal bars. */
export function BreakdownChart({
  data,
  title,
  description,
  metric,
}: {
  data: Dashboard;
  title: string;
  description: string;
  metric: "grossProfit" | "units";
}) {
  const [groupBy, setGroupBy] = useState<GroupBy>("supplier");
  const rows: DashboardBreakdown[] = [
    ...(groupBy === "supplier" ? data.bySupplier : data.byCategory),
  ]
    .sort((a, b) => b[metric] - a[metric])
    .slice(0, 8);
  const format = (value: number | string) =>
    metric === "units"
      ? Number(value).toLocaleString()
      : aedRounded(Number(value));

  return (
    <Widget>
      <Widget.Header>
        <div className="flex flex-col">
          <Widget.Title>{title}</Widget.Title>
          <Widget.Description>{description}</Widget.Description>
        </div>
        <Segment
          aria-label="Group by"
          selectedKey={groupBy}
          size="sm"
          onSelectionChange={(key: Key) => setGroupBy(key as GroupBy)}
        >
          <Segment.Item id="supplier">Supplier</Segment.Item>
          <Segment.Item id="category">
            <Segment.Separator />
            Category
          </Segment.Item>
        </Segment>
      </Widget.Header>
      <Widget.Content>
        {rows.length === 0 ? (
          <p className="text-muted py-16 text-center text-sm">
            Nothing in this range
          </p>
        ) : (
          <BarChart
            data={rows}
            height={Math.max(160, rows.length * 36)}
            layout="vertical"
            margin={{ top: 0, right: 16, bottom: 0, left: 0 }}
          >
            <BarChart.XAxis hide type="number" />
            <BarChart.YAxis
              {...axis}
              dataKey="name"
              type="category"
              width={140}
            />
            <BarChart.Tooltip
              content={<BarChart.TooltipContent valueFormatter={format} />}
              cursor={{ fill: "var(--surface-secondary)" }}
            />
            <BarChart.Bar
              barSize={18}
              dataKey={metric}
              fill={SERIES_A}
              name={metric === "units" ? "Units" : "Gross profit"}
              radius={[0, 4, 4, 0]}
            />
          </BarChart>
        )}
      </Widget.Content>
    </Widget>
  );
}
